import React, { useState, useEffect } from 'react';
import { Row, Col } from 'reactstrap';
import { DropDownTree } from '@progress/kendo-react-dropdowns';
import { Input } from '@progress/kendo-react-inputs';
import { ButtonGroup, Button } from '@progress/kendo-react-buttons';
import { filterBy } from '@progress/kendo-react-data-tools';
import { mapTree, extendDataItem } from '@progress/kendo-react-common';
import { AccentButton } from "../controls/AccentButton";
import { t, showOK } from "../services/HelperService";
import { AccentDialog } from "./AccentDialog";
import { AccentDisplay } from '../controls/AccentDisplay'
import AccentIcons from '../icons/Icons'
import { update } from '../services/DataService';

export const SetTemplateParameterValuesDlg = props => {

    const [parameters, setParameters] = useState([]);
    const [undoEnabled, setUndoEnabled] = useState(false);
    const [saveEnabled, setSaveEnabled] = useState(false);
    const [didSave, setDidSave] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        var params = await props.query();

        // Initialize UI mode for each parameter based on type and existing value
        params = params.map(p => {
            const isUrlParam = p.Type?.toLowerCase() === 'url' ||
                p.Name?.toLowerCase().includes('button.url');

            let uiMode = 'merge'; // default

            if (isUrlParam && p.Value) {
                // Detect mode from existing value - if it's a CSS link, set to css mode
                if (p.Value.startsWith('{{CSS.') || p.Value.startsWith('{{css:') || p.Value.startsWith('{{css "')) {
                    uiMode = 'css';
                }
            }

            return {
                ...p,
                UiMode: uiMode,
                IsUrlParam: isUrlParam
            };
        });

        setParameters(params);
    }

    const onUndo = () => {
        fetchData();
        setSaveEnabled(false);
        setUndoEnabled(false);
    }

    const onSave = async () => {
        await update("SetTemplateParamsValue", parameters, false);

        setSaveEnabled(false);
        setUndoEnabled(false);
        setDidSave(true);
    }

    const promptUnsaved = () => {
        const buttons = [
            "application_strings.application.buttons.discardChanges",
            "application_strings.application.buttons.editChanges",
            "application_strings.application.buttons.saveChanges"
        ];

        return showOK(
            "application_strings.application.dialogs.unsavedchangesTitle",
            "application_strings.application.dialogs.unsavedchanges",
            buttons,
            false,
            null,
            true,
            400
        );
    }

    const onClose = async () => {
        var hasUnsavedChanges = undoEnabled || saveEnabled;
        if (hasUnsavedChanges) {
            const choice = await promptUnsaved();

            if (choice === "application_strings.application.buttons.discardChanges") {
                props.onClose({ canceled: true });
            } else if (choice === "application_strings.application.buttons.saveChanges") {
                await onSave();
                props.onClose({ canceled: false });
            }

            return;
        }

        if (didSave) {
            props.onClose({ canceled: false });
        } else {
            props.onClose({ canceled: true });
        }
    }

    const onChangeParamValue = (id, e) => {
        var newValue = props.lookupFieldPlaceHolderFromId(e);

        var params = [...parameters];
        var param = params.find(p => p.ID === id);
        param.Value = newValue;

        setParameters(params);
        setUndoEnabled(true);
        setSaveEnabled(true);
    };

    const onChangeCssLink = (id, cssType) => {
        var params = [...parameters];
        var param = params.find(p => p.ID === id);

        // Map CSS type to merge field
        const cssMap = {
            'ACCEPT': '{{css "ACCEPT"}}',
            'ACCEPT_EXISTING': '{{css "ACCEPT_EXISTING"}}',
            // Preserve existing behavior (note: these labels appear swapped historically)
            'FINAL_PAYMENT': '{{css "DISPATCH_PAYMENT"}}',
            'DISPATCH_PAYMENT': '{{css "FINAL_PAYMENT"}}'
        };

        param.Value = cssMap[cssType] || cssType;

        setParameters(params);
        setUndoEnabled(true);
        setSaveEnabled(true);
    };

    const onSetAutoGenerated = (id) => {
        var params = [...parameters];
        var param = params.find(p => p.ID === id);

        // Use a special marker to indicate auto-generation
        param.Value = '{{AUTO_GENERATED}}';

        setParameters(params);
        setUndoEnabled(true);
        setSaveEnabled(true);
    };

    if (!props.fieldItems) return null;

    const mapTreeData = (items) => {
        return React.Children.toArray(items)
            .map(item => ({
                id: item.props.data?.id || item.props.text,
                text: item.props.text,
                items: item.props.children ? mapTreeData(item.props.children) : []
            }));
    };

    const fieldTreeData = mapTreeData(props.fieldItems);

    var parameterCtrls = parameters.map(p => {
        return (
            <Row key={p.ID} style={{ marginBottom: '20px', paddingBottom: '15px', borderBottom: '1px solid #e0e0e0' }}>
                <Col md={12}>
                    <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '14px' }}>
                        {p.Name}
                        {p.IsUrlParam && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#666', fontWeight: 'normal' }}>(URL Parameter)</span>}
                    </div>

                    {p.IsUrlParam ? (
                        <UrlParameterControl
                            parameter={p}
                            onChangeCssLink={(cssType) => onChangeCssLink(p.ID, cssType)}
                            onSetAutoGenerated={() => onSetAutoGenerated(p.ID)}
                        />
                    ) : (
                        <StandardParameterControl
                            parameter={p}
                            fieldTreeData={fieldTreeData}
                            onChangeParamValue={(e) => onChangeParamValue(p.ID, e)}
                            lookupIdFromFieldPlaceHolder={props.lookupIdFromFieldPlaceHolder}
                        />
                    )}
                </Col>
            </Row>
        );
    });

    return <AccentDialog
        headerContent={t(props.title)}
        relativeWidth={display => display.large ? display.width / 2 : display.width}
        onClose={props.onClose}
        maxWidth={900}
        actionsContent={[
            <AccentButton key="3" tagName="undoBtn" onClick={onUndo} disabled={!undoEnabled}>
                <AccentDisplay large tablet>
                    {t("application_strings.application.buttons.undo")}
                </AccentDisplay>
                <AccentDisplay mobile>
                    <AccentIcons.Undo />
                </AccentDisplay>
            </AccentButton>,
            <AccentButton key="4" tagName="saveBtn" onClick={onSave} disabled={!saveEnabled}>
                <AccentDisplay large tablet>
                    {t("application_strings.application.buttons.save")}
                </AccentDisplay>
                <AccentDisplay mobile>
                    <AccentIcons.Save />
                </AccentDisplay>
            </AccentButton>,
            <AccentButton key="5" tagName="closeBtn" onClick={onClose}>
                <AccentDisplay large tablet>
                    {t("application_strings.application.buttons.close")}
                </AccentDisplay>
                <AccentDisplay mobile>
                    <AccentIcons.Close />
                </AccentDisplay>
            </AccentButton>
        ]}
    >
        <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '10px' }}>
            {parameterCtrls}
        </div>
    </AccentDialog>;
};

// Component for URL parameters with CSS link selection
const UrlParameterControl = (props) => {
    const { parameter, onChangeCssLink, onSetAutoGenerated } = props;

    const isCssLink = parameter.Value && (
        parameter.Value.startsWith('{{CSS.') ||
        parameter.Value.startsWith('{{css:') ||
        parameter.Value.startsWith('{{css "')
    );
    const isAutoGenerated = parameter.Value === '{{AUTO_GENERATED}}';

    const getCssTokenType = (val) => {
        if (!val) return null;
        // legacy: {{css:TYPE}}
        let m = val.match(/\{\{\s*css:([A-Za-z0-9_]+)\s*\}\}/);
        if (m && m[1]) return m[1];
        // handlebars helper: {{css "TYPE"}}
        m = val.match(/\{\{\s*css\s+"([^"]+)"\s*\}\}/);
        if (m && m[1]) return m[1];
        return null;
    };

    // NOTE: historically FINAL/DISPATCH labels map to opposite tokens (preserved for backwards-compat).
    const isTokenSelected = (tokenType) => getCssTokenType(parameter.Value) === tokenType;

    return (
        <div>
            {/* CSS Link Setup Banner */}
            {isCssLink && (
                <div style={{
                    fontSize: '12px',
                    color: '#1565c0',
                    marginBottom: '16px',
                    padding: '12px',
                    backgroundColor: '#e3f2fd',
                    border: '1px solid #64b5f6',
                    borderRadius: '4px',
                    lineHeight: '1.6'
                }}>
                    <div style={{ fontWeight: '600', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>ℹ️</span>
                        <span>WhatsApp Manager Setup Required</span>
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                        For CSS links to work, configure your WhatsApp template's URL button as:
                    </div>
                    <div style={{
                        backgroundColor: '#fff',
                        padding: '8px 10px',
                        borderRadius: '3px',
                        border: '1px solid #90caf9',
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        wordBreak: 'break-all',
                        marginBottom: '8px'
                    }}>
                        https://MYBUSINESSUNIT.css.com/Customer/App/&#123;&#123;1&#125;&#125;
                    </div>
                    <div style={{ fontSize: '11px', fontStyle: 'italic', color: '#0d47a1' }}>
                        The system will replace &#123;&#123;1&#125;&#125; with the actual CSS link when sending
                    </div>
                </div>
            )}

            <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                    Select URL type:
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                    <button
                        onClick={() => onChangeCssLink('ACCEPT')}
                        style={{
                            padding: '12px',
                            fontSize: '13px',
                            border: '1px solid',
                            borderColor: isTokenSelected('ACCEPT') ? '#1976d2' : '#ccc',
                            backgroundColor: isTokenSelected('ACCEPT') ? '#e3f2fd' : 'white',
                            color: isTokenSelected('ACCEPT') ? '#1565c0' : '#333',
                            fontWeight: isTokenSelected('ACCEPT') ? '600' : 'normal',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                            if (!isTokenSelected('ACCEPT')) {
                                e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isTokenSelected('ACCEPT')) {
                                e.currentTarget.style.backgroundColor = 'white';
                            }
                        }}
                    >
                        CSS ACCEPT
                    </button>
                    <button
                        onClick={() => onChangeCssLink('ACCEPT_EXISTING')}
                        style={{
                            padding: '12px',
                            fontSize: '13px',
                            border: '1px solid',
                            borderColor: isTokenSelected('ACCEPT_EXISTING') ? '#1976d2' : '#ccc',
                            backgroundColor: isTokenSelected('ACCEPT_EXISTING') ? '#e3f2fd' : 'white',
                            color: isTokenSelected('ACCEPT_EXISTING') ? '#1565c0' : '#333',
                            fontWeight: isTokenSelected('ACCEPT_EXISTING') ? '600' : 'normal',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                            if (!isTokenSelected('ACCEPT_EXISTING')) {
                                e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isTokenSelected('ACCEPT_EXISTING')) {
                                e.currentTarget.style.backgroundColor = 'white';
                            }
                        }}
                    >
                        CSS ACCEPT (EXISTING)
                    </button>
                    <button
                        onClick={() => onChangeCssLink('FINAL_PAYMENT')}
                        style={{
                            padding: '12px',
                            fontSize: '13px',
                            border: '1px solid',
                            borderColor: isTokenSelected('DISPATCH_PAYMENT') ? '#1976d2' : '#ccc',
                            backgroundColor: isTokenSelected('DISPATCH_PAYMENT') ? '#e3f2fd' : 'white',
                            color: isTokenSelected('DISPATCH_PAYMENT') ? '#1565c0' : '#333',
                            fontWeight: isTokenSelected('DISPATCH_PAYMENT') ? '600' : 'normal',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                            if (!isTokenSelected('DISPATCH_PAYMENT')) {
                                e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isTokenSelected('DISPATCH_PAYMENT')) {
                                e.currentTarget.style.backgroundColor = 'white';
                            }
                        }}
                    >
                        CSS FINAL PAYMENT
                    </button>
                    <button
                        onClick={() => onChangeCssLink('DISPATCH_PAYMENT')}
                        style={{
                            padding: '12px',
                            fontSize: '13px',
                            border: '1px solid',
                            borderColor: isTokenSelected('FINAL_PAYMENT') ? '#1976d2' : '#ccc',
                            backgroundColor: isTokenSelected('FINAL_PAYMENT') ? '#e3f2fd' : 'white',
                            color: isTokenSelected('FINAL_PAYMENT') ? '#1565c0' : '#333',
                            fontWeight: isTokenSelected('FINAL_PAYMENT') ? '600' : 'normal',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                            if (!isTokenSelected('FINAL_PAYMENT')) {
                                e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isTokenSelected('FINAL_PAYMENT')) {
                                e.currentTarget.style.backgroundColor = 'white';
                            }
                        }}
                    >
                        CSS DISPATCH PAYMENT
                    </button>
                </div>
            </div>

            <div style={{
                borderTop: '1px solid #e0e0e0',
                paddingTop: '12px',
                marginTop: '12px'
            }}>
                <button
                    onClick={onSetAutoGenerated}
                    style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '13px',
                        border: '1px solid',
                        borderColor: isAutoGenerated ? '#1976d2' : '#ccc',
                        backgroundColor: isAutoGenerated ? '#e3f2fd' : 'white',
                        color: isAutoGenerated ? '#1565c0' : '#333',
                        fontWeight: isAutoGenerated ? '600' : 'normal',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                        if (!isAutoGenerated) {
                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isAutoGenerated) {
                            e.currentTarget.style.backgroundColor = 'white';
                        }
                    }}
                >
                    Auto-Generated URL
                </button>
                <div style={{
                    fontSize: '11px',
                    color: '#666',
                    marginTop: '6px',
                    fontStyle: 'italic'
                }}>
                    System will generate URL at send-time (e.g., PDF links, tracking links)
                </div>
            </div>
        </div>
    );
};

// Component for standard (non-URL) parameters
const StandardParameterControl = (props) => {
    const { parameter, fieldTreeData, onChangeParamValue, lookupIdFromFieldPlaceHolder } = props;

    const findNodeById = (nodes, id) => {
        for (const node of nodes) {
            if (node.id === id) return node;
            if (node.items) {
                const child = findNodeById(node.items, id);
                if (child) return child;
            }
        }
        return null;
    };

    const fieldId = lookupIdFromFieldPlaceHolder(parameter.Value);
    const selectedNode = findNodeById(fieldTreeData, fieldId);

    return (
        <ParameterValueTree
            data={fieldTreeData}
            value={selectedNode}
            onChange={onChangeParamValue}
        />
    );
};

export const ParameterValueTree = props => {

    const selectField = 'selected';
    const expandField = 'expanded';
    const dataItemKey = 'id';
    const textField = 'text';
    const subItemsField = 'items';
    const fields = {
        selectField,
        expandField,
        dataItemKey,
        subItemsField
    };

    const [data, setData] = React.useState();
    const [value, setValue] = React.useState();
    const [treeData, setTreeData] = React.useState([]);
    const [expanded, setExpanded] = React.useState([]);

    useEffect(() => {
        setData(props.data || [])
        setValue(props.value || null)
    }, [props.data, props.value]);

    useEffect(() => {
        if (data && data.length > 0) {
            var d =
                processTreeData(
                    data,
                    {
                        expanded,
                        value
                    },
                    fields
                );
            setTreeData(d);
        }
    }, [data, expanded, value]);

    const onChange = (event) => {
        setValue(event.value);
        if (props.onChange) props.onChange(event.value ? event.value.id : event.value);
    };

    const onExpandChange = React.useCallback(event => setExpanded(expandedState(event.item, dataItemKey, expanded)), [expanded]);

    if (!treeData) return null;

    return (
        <DropDownTree
            key={props.key}
            data={treeData}
            dataItemKey={dataItemKey}
            textField={textField}
            childrenField={subItemsField}
            onChange={onChange}
            selectField={selectField}
            expandField={expandField}
            onExpandChange={onExpandChange}
            value={value}
            placeholder='Select item'
        />
    )
}

export const processTreeData = (data, state, fields) => {
    const {
        selectField,
        expandField,
        dataItemKey,
        subItemsField
    } = fields;
    const {
        expanded,
        value,
        filter
    } = state;
    const filtering = Boolean(filter && filter.value);
    return mapTree(filtering ? filterBy(data, [filter], subItemsField) : data, subItemsField, item => {
        const props = {
            [expandField]: expanded.includes(item[dataItemKey]),
            [selectField]: value && item[dataItemKey] === value[dataItemKey]
        };
        return filtering ? extendDataItem(item, subItemsField, props) : {
            ...item,
            ...props
        };
    });
};

export const expandedState = (item, dataItemKey, expanded) => {
    const nextExpanded = expanded.slice();
    const itemKey = item[dataItemKey];
    const index = expanded.indexOf(itemKey);
    index === -1 ? nextExpanded.push(itemKey) : nextExpanded.splice(index, 1);
    return nextExpanded;
};